import { useEffect, useState } from "react";

import { Button, Container, DialogContentText, Grid, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { Box } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

import Spinner from "../utils/Spinner";
import InfoChip from "../utils/InfoChip";
import Providers from "./Providers";
import UploadLogo from "./UploadLogo";
import ProviderAdd from "./ProviderAdd";
import { useAuth } from "../auth/AuthProvider";
import SectionHeader from "../utils/SectionHeader";
import ProtectedRoute from "../auth/ProtectedRoute";
import ButtonWithConfirm from "../utils/ButtonWithConfirm";
import CustomTermsHistory from "./CustomTermsHistory";
import PartNumberCheckboxes from "./PartNumberCheckboxes";
import {
  Organization,
  useConfgOrganizationEditSettings,
  useConfgOrganizationEditTerms,
  useConfgOrganizationSettings
} from "../../hooks/confg/base";

const domainIcon = (
  <InputAdornment position="end">
    <InfoChip size="medium" info="This attribute is used to specify identity provider validity." />
  </InputAdornment>
);

type OrgViewProps = {
  organization: Organization;
};

const OrgView = (props: OrgViewProps) => {
  const { organization } = props;

  const auth = useAuth();
  const isAdmin = ["org.admin", "sys.admin"].includes(auth.user.activeOrganization.role);

  const [locked, setLocked] = useState(false);
  const [name, setName] = useState<string>("");
  const [domain, setDomain] = useState<string>("");
  const [acronym, setAcronym] = useState<string>("");
  const [urlPublic, setUrlPublic] = useState<string>("");
  const [urlPrivate, setUrlPrivate] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [partNumber, setPartNumber] = useState<Organization["partNumber"]>({
    unique: false,
    required: false
  });

  const [lockedTerms, setLockedTerms] = useState(false);
  const [currentTerms, setCurrentTerms] = useState<string>("");

  const terms = useConfgOrganizationEditTerms();
  const settings = useConfgOrganizationEditSettings();

  const handleEditSettings = () => {
    settings.mutate({
      name,
      domain,
      acronym,
      urlPublic,
      urlPrivate,
      partNumber,
      description
    });
  };

  const handleEditTerms = () => {
    terms.mutate({ text: currentTerms });
  };

  const resetTerms = () => {
    setLockedTerms(false);
    setCurrentTerms(organization?.terms?.find((terms) => !!terms.current)?.text || "");
  };

  const resetForm = () => {
    setLocked(false);

    setName(organization.name);
    setDomain(organization.domain);
    setAcronym(organization.acronym);
    setUrlPublic(organization.url.public);
    setUrlPrivate(organization.url.private);
    setDescription(organization.description);
    setPartNumber({
      unique: organization.partNumber.unique,
      required: organization.partNumber.required
    });

    resetTerms();
  };

  useEffect(() => {
    resetForm();
  }, [organization]);

  useEffect(() => {
    if (terms.isSuccess) resetForm();
  }, [terms.isSuccess]);

  useEffect(() => {
    if (settings.isSuccess) resetForm();
  }, [settings.isSuccess]);

  return (
    <Container maxWidth="xl" sx={{ marginBottom: 4 }}>
      <SectionHeader
        key="organization-settings-header"
        title={organization.name}
        icon={<SettingsIcon />}
        buttons={[
          <Button key={"organization-settings-reset"} color="inherit" size="small" disabled={locked} onClick={resetForm}>
            Reset
          </Button>
        ]}
      />

      <Box component="form" noValidate>
        <Typography gutterBottom variant="h6" component="div">
          General
        </Typography>

        <DialogContentText variant="caption">
          To configure general information for this organization please make changes, then submit. Changes will not take
          effect until submitted & confirmed. Note: Will ask for confirmation.
        </DialogContentText>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              disabled
              fullWidth
              id="organization-name"
              name="organization-name"
              label="Name"
              value={name}
              onChange={(e: any) => setName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="organization-domain"
              name="organization-domain"
              label="Domain"
              disabled={locked}
              value={domain}
              onChange={(e: any) => setDomain(e.target.value)}
              InputProps={{ endAdornment: domainIcon }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="organization-acronym"
              name="organization-acronym"
              label="Acronym"
              disabled={locked}
              value={acronym}
              onChange={(e: any) => setAcronym(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="organization-description"
              name="organization-description"
              label="Description"
              disabled={locked}
              value={description}
              onChange={(e: any) => setDescription(e.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              disabled
              fullWidth
              id="organization-public-url"
              name="organization-public-url"
              label="Public URL"
              value={urlPublic}
              onChange={(e: any) => setUrlPublic(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              disabled
              fullWidth
              id="organization-private-url"
              name="organization-private-url"
              label="Private URL"
              value={urlPrivate}
              onChange={(e: any) => setUrlPrivate(e.target.value)}
            />
          </Grid>
        </Grid>

        <Typography gutterBottom variant="h6" component="div" sx={{ mt: 2 }}>
          Attributes
        </Typography>

        <Grid item xs={12}>
          <DialogContentText variant="caption">
            To configure workflow requirements for this organization please make changes, then submit. Changes will not take
            effect until submitted & confirmed. Note: Will ask for confirmation.
          </DialogContentText>
        </Grid>

        <Grid item xs={12} sm={6}>
          <PartNumberCheckboxes disabled={locked} partNumber={partNumber} setPartNumber={setPartNumber} />
        </Grid>

        <Grid item xs={12}>
          <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end">
            <Button variant={locked ? "outlined" : "contained"} onClick={() => setLocked(!locked)} sx={{ mt: 3, mb: 2 }}>
              {locked ? "Cancel" : "Save"}
            </Button>

            <Button variant="contained" disabled={!locked} onClick={handleEditSettings} sx={{ mt: 3, mb: 2 }}>
              {settings.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Submit"}
            </Button>
          </Stack>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography gutterBottom variant="h6" component="div">
                IDP
              </Typography>

              {isAdmin && <ProviderAdd key={"provider-add"} />}
            </Stack>

            <DialogContentText variant="caption">
              To configure identity provider(s) for this organization please select IDP(s), then submit. Credentials must be
              provided for each identity provider. Changes will not take effect until submitted & confirmed. Note: Will ask
              for confirmation.
            </DialogContentText>
          </Grid>

          <Grid item xs={12}>
            <Providers />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography gutterBottom variant="h6" component="div">
                Terms & Conditions
              </Typography>

              <Button
                key={"organization-terms-reset"}
                color="inherit"
                size="small"
                disabled={lockedTerms}
                onClick={resetTerms}
              >
                Reset
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <DialogContentText variant="caption">
              To configure terms & conditions for this organization please make changes, then submit. Changes will not take
              effect until submitted & confirmed. Note: Will ask for confirmation.
            </DialogContentText>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={10}
              disabled={lockedTerms}
              value={currentTerms}
              onChange={(e) => setCurrentTerms(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end">
              <Button variant={lockedTerms ? "outlined" : "contained"} onClick={() => setLockedTerms(!lockedTerms)}>
                {lockedTerms ? "Cancel" : "Save"}
              </Button>

              <ButtonWithConfirm
                type="Terms & Conditions"
                label="Submit"
                color="primary"
                status={terms.status}
                disabled={!lockedTerms}
                message={
                  <>
                    WARNING: Please note that confirming this action will create a new current version. This action cannot be
                    undone.
                  </>
                }
                isLoading={terms.isPending}
                onConfirm={handleEditTerms}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <CustomTermsHistory organization={organization} />
          </Grid>

          {/* <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography gutterBottom variant="h6" component="div">
                Logo
              </Typography>

              <Button
                key={"organization-logo-reset"}
                size="small"
                color="inherit"
              >
                Reset
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <UploadLogo />
          </Grid> */}
        </Grid>
      </Box>
    </Container>
  );
};

const Org = () => {
  const organization = useConfgOrganizationSettings();

  return (
    <ProtectedRoute>
      {organization.isPending ? (
        <Spinner />
      ) : organization.isSuccess ? (
        <OrgView organization={organization.data.data} />
      ) : (
        <Typography variant="caption">No organization settings found.</Typography>
      )}
    </ProtectedRoute>
  );
};

export default Org;
